import {
  ComponentConfig,
  LayoutTypes,
  ComponentType,
  FlexDirection,
  InputType,
  ValidationRule,
} from "../services/types/ComponentTypes";

const RETURN_TAXABLE_EXPR =
  "(([txt_amount] - [txt_line_discount]) / [txt_inv_qty]) * [txt_return_qty]";
const RETURN_VAT_EXPR =
  `${RETURN_TAXABLE_EXPR} * ([drp_vat.taxrate]/100)`;
const RETURN_NET_EXPR = `${RETURN_TAXABLE_EXPR} + [txt_vat_amt]`;

/** Legacy screen fields use BindValue; strict types require `name`. */
function legacyField<T extends { BindValue: string }>(field: T): ComponentConfig {
  return { ...field, name: field.BindValue } as unknown as ComponentConfig;
}

const hiddenSrcLineField: ComponentConfig = legacyField({
  IncludeLabel: false,
  Label: "",
  BindValue: "txt_src_line_id",
  Json_Attribute: "src_line_id",
  componentType: ComponentType.TEXT_FIELD,
  IsHidden: true,
  IsReadOnly: true,
  inputType: InputType.TEXT,
});

const invoiceAmountFields: ComponentConfig[] = [
  legacyField({
    IncludeLabel: true,
    Label: "Qty",
    BindValue: "txt_inv_qty",
    Json_Attribute: "inv_qty",
    componentType: ComponentType.TEXT_FIELD,
    IsReadOnly: true,
    Default_Value: "0",
    inputType: InputType.NUMBER,
  }),
  legacyField({
    IncludeLabel: true,
    Label: "Rate",
    BindValue: "txt_rate",
    Json_Attribute: "rate",
    componentType: ComponentType.TEXT_FIELD,
    IsReadOnly: true,
    Default_Value: "0",
    inputType: InputType.NUMBER,
  }),
  legacyField({
    IncludeLabel: true,
    Label: "Unit discount",
    BindValue: "txt_unit_discount",
    Json_Attribute: "unit_discount",
    componentType: ComponentType.TEXT_FIELD,
    IsReadOnly: true,
    Default_Value: "0",
    inputType: InputType.NUMBER,
  }),
  legacyField({
    IncludeLabel: true,
    Label: "Total discount",
    BindValue: "txt_line_discount",
    Json_Attribute: "line_discount",
    componentType: ComponentType.TEXT_FIELD,
    IsReadOnly: true,
    Default_Value: "0",
    inputType: InputType.NUMBER,
  }),
  legacyField({
    IncludeLabel: true,
    Label: "Amount",
    BindValue: "txt_amount",
    Json_Attribute: "amount",
    componentType: ComponentType.TEXT_FIELD,
    IsReadOnly: true,
    Default_Value: "0",
    inputType: InputType.NUMBER,
  }),
  legacyField({
    IncludeLabel: true,
    Label: "VAT%",
    BindValue: "drp_vat",
    Json_Attribute: "vat_id",
    componentType: ComponentType.SELECT_DROPDOWN,
    LookupName: "tax_rates",
    disabled: true,
  }),
  legacyField({
    IncludeLabel: true,
    Label: "Net amount",
    BindValue: "txt_net_amt",
    Json_Attribute: "net_amt",
    componentType: ComponentType.TEXT_FIELD,
    IsReadOnly: true,
    Default_Value: "0",
    inputType: InputType.NUMBER,
  }),
];

const returnQtyValidation = [
  {
    rule: ValidationRule.PATTERN,
    value: "^[0-9]*$",
    message: "Only numbers are allowed",
  },
  {
    rule: ValidationRule.CONDITION,
    condition: "[return_qty] <= [inv_qty]",
    message: "Return quantity cannot be greater than invoice quantity",
  },
];

const returnEntryFields: ComponentConfig[] = [
  legacyField({
    IncludeLabel: true,
    Label: "Return qty",
    BindValue: "txt_return_qty",
    Json_Attribute: "return_qty",
    componentType: ComponentType.TEXT_FIELD,
    Default_Value: "0",
    inputType: InputType.NUMBER,
    validation: returnQtyValidation,
  }),
  legacyField({
    IncludeLabel: true,
    Label: "Return VAT",
    BindValue: "txt_vat_amt",
    Json_Attribute: "vat_amt",
    componentType: ComponentType.TEXT_FIELD,
    IsReadOnly: true,
    ExpressionV2: RETURN_VAT_EXPR,
    Default_Value: "0",
    inputType: InputType.NUMBER,
  }),
  legacyField({
    IncludeLabel: true,
    Label: "Return net",
    BindValue: "txt_return_net_amt",
    Json_Attribute: "return_net_amt",
    componentType: ComponentType.TEXT_FIELD,
    IsReadOnly: true,
    ExpressionV2: RETURN_NET_EXPR,
    Default_Value: "0",
    inputType: InputType.NUMBER,
  }),
  legacyField({
    IncludeLabel: true,
    Label: "Department",
    BindValue: "drp_department",
    Json_Attribute: "department_id",
    componentType: ComponentType.SELECT_DROPDOWN,
    LookupName: "department",
  }),
];

function buildReturnFooter(
  returnNetJsonAttr: string,
  returnVatJsonAttr: string
): ComponentConfig[] {
  return [
    legacyField({
      componentType: ComponentType.TEXT_FIELD,
      BindValue: "txt_footer_pad",
      IncludeLabel: false,
      IsReadOnly: true,
      style: { display: "none" },
    }),
    legacyField({
      IncludeLabel: false,
      Label: "Section return VAT",
      BindValue: "txt_return_vat_total",
      Json_Attribute: returnVatJsonAttr,
      componentType: ComponentType.TEXT_FIELD,
      IsReadOnly: true,
      ExpressionV2: "[SUM:txt_vat_amt]",
      Default_Value: "0",
      inputType: InputType.NUMBER,
    }),
    legacyField({
      IncludeLabel: false,
      Label: "Section return total",
      BindValue: "txt_return_net_total",
      Json_Attribute: returnNetJsonAttr,
      componentType: ComponentType.TEXT_FIELD,
      IsReadOnly: true,
      ExpressionV2: "[SUM:txt_return_net_amt]",
      Default_Value: "0",
      inputType: InputType.NUMBER,
    }),
  ];
}

function buildSpareSubmodule(): ComponentConfig {
  return {
    componentType: ComponentType.SUB_MODULE,
    name: "sales_return_spareparts",
    label: "Spare parts",
    tableName: "sales_return_spareparts",
    lookupName: "sales_return_invoice_spareparts",
    parentField: "drp_sales_ret",
    BindValue: "sales_return_spareparts",
    DisableAddNewRow: true,
    childControls: [
      hiddenSrcLineField,
      legacyField({
        IncludeLabel: true,
        Label: "Code",
        BindValue: "drp_code",
        Json_Attribute: "codeid",
        componentType: ComponentType.SEARCHABLE_DROPDOWN,
        LookupName: "product_itemcode",
        disabled: true,
      }),
      legacyField({
        IncludeLabel: true,
        Label: "Narration",
        BindValue: "txt_narration",
        Json_Attribute: "narration",
        componentType: ComponentType.TEXT_FIELD,
        IsReadOnly: true,
        inputType: InputType.TEXT,
      }),
      ...invoiceAmountFields,
      ...returnEntryFields,
    ],
    FooterControls: buildReturnFooter("return_spare_net", "return_spare_vat"),
  };
}

function buildLabourSubmodule(): ComponentConfig {
  return {
    componentType: ComponentType.SUB_MODULE,
    name: "sales_return_labour",
    label: "Labour/Others",
    tableName: "sales_return_labour",
    lookupName: "sales_return_invoice_labour",
    parentField: "drp_sales_ret",
    BindValue: "sales_return_labour",
    DisableAddNewRow: true,
    childControls: [
      hiddenSrcLineField,
      legacyField({
        IncludeLabel: true,
        Label: "Job type",
        BindValue: "drp_jobtype",
        Json_Attribute: "jobtypeid",
        componentType: ComponentType.SELECT_DROPDOWN,
        LookupName: "itemgroup",
        disabled: true,
      }),
      legacyField({
        IncludeLabel: true,
        Label: "Code",
        BindValue: "drp_code",
        Json_Attribute: "codeid",
        componentType: ComponentType.SEARCHABLE_DROPDOWN,
        LookupName: "itemcode",
        disabled: true,
      }),
      legacyField({
        IncludeLabel: true,
        Label: "Narration",
        BindValue: "txt_narration",
        Json_Attribute: "narration",
        componentType: ComponentType.TEXT_FIELD,
        IsReadOnly: true,
        inputType: InputType.TEXT,
      }),
      ...invoiceAmountFields,
      ...returnEntryFields,
    ],
    FooterControls: buildReturnFooter("return_labour_net", "return_labour_vat"),
  };
}

function buildMiscSubmodule(): ComponentConfig {
  return {
    componentType: ComponentType.SUB_MODULE,
    name: "sales_return_miscellaneous",
    label: "Miscellaneous",
    tableName: "sales_return_miscellaneous",
    lookupName: "sales_return_invoice_miscellaneous",
    parentField: "drp_sales_ret",
    BindValue: "sales_return_miscellaneous",
    DisableAddNewRow: true,
    childControls: [
      hiddenSrcLineField,
      legacyField({
        IncludeLabel: true,
        Label: "Item type",
        BindValue: "txt_item_type",
        Json_Attribute: "item_type",
        componentType: ComponentType.TEXT_FIELD,
        IsReadOnly: true,
        inputType: InputType.TEXT,
      }),
      legacyField({
        IncludeLabel: true,
        Label: "Item name",
        BindValue: "txt_item_name",
        Json_Attribute: "item_name",
        componentType: ComponentType.TEXT_FIELD,
        IsReadOnly: true,
        inputType: InputType.TEXT,
      }),
      ...invoiceAmountFields,
      ...returnEntryFields,
    ],
    FooterControls: buildReturnFooter("return_misc_net", "return_misc_vat"),
  };
}

export const screen_124_add_sales_return_voucher: ComponentConfig[] = [
  {
    componentType: LayoutTypes.SCREEN_GROUP,
    title: "Sales return voucher",
    style: {},
    childComponents: [
      {
        componentType: LayoutTypes.GROUP,
        style: {
          direction: FlexDirection.ROW,
          gap: 8,
          marginBottom: 8,
        },
        childComponents: [
          {
            componentType: ComponentType.BUTTON,
            Label: "Print compact",
            BindValue: "btn_print_compact",
            ActionType: "PDF",
            Settings: "sales_return_voucher_basic",
            style: {
              backgroundColor: "#374151",
              textColor: "#FFFFFF",
              padding: 10,
              borderRadius: 8,
            },
          },
          {
            componentType: ComponentType.BUTTON,
            Label: "Complete print",
            BindValue: "btn_print_complete",
            ActionType: "PDF",
            Settings: "sales_return_voucher",
            style: {
              backgroundColor: "#374151",
              textColor: "#FFFFFF",
              padding: 10,
              borderRadius: 8,
            },
          },
        ],
      },
      {
        componentType: LayoutTypes.FORM,
        Name: "sales_ret_add",
        TableName: "sales_return",
        DisplayName: "sales_return",
        Plugins: [{ type: "api", module: "all" }],
        columnNumber: "m1 t1 w1",
        style: {
          padding: 10,
          backgroundColor: "#ffffff",
          direction: FlexDirection.COLUMN,
        },
        Fields: [
          {
            componentType: LayoutTypes.FIELD_GROUP,
            Label: "Sales return details",
            BindValue: "con_sales_return_details",
            style: { direction: FlexDirection.COLUMN },
            childComponents: [
              legacyField({
                IncludeLabel: true,
                Label: "Invoice  No.",
                PlaceHolder: "",
                IsMandatory: true,
                BindValue: "drp_sales_ret",
                Json_Attribute: "invoice_id",
                componentType: ComponentType.SEARCHABLE_DROPDOWN,
                showInSummary: true,
                LookupName: "sales_invoice",
                IsVerticalView: true,
                validation: [
                  {
                    rule: ValidationRule.REQUIRED,
                    message: "Invoice No. is required",
                  },
                ],
              }),
              legacyField({
                IncludeLabel: true,
                Label: "Return No.",
                BindValue: "txt_sales_return_no",
                Json_Attribute: "sales_return_no",
                componentType: ComponentType.TEXT_FIELD,
                AutoPopulate: true,
                IsReadOnly: true,
                AutoPopulate_format: "SALRET_[******]",
                IsVerticalView: true,
                inputType: InputType.TEXT,
              }),
              legacyField({
                IncludeLabel: true,
                Label: "Date",
                IsMandatory: true,
                BindValue: "txt_sales_date",
                Json_Attribute: "sales_return_date",
                componentType: ComponentType.DATE_FIELD,
                DefaultToday: true,
                IsVerticalView: true,
                validation: [
                  {
                    rule: ValidationRule.REQUIRED,
                    message: "Date is required",
                  },
                ],
              }),
              legacyField({
                IncludeLabel: true,
                Label: "Mobile",
                BindValue: "txt_mobile",
                Json_Attribute: "mobile_no",
                componentType: ComponentType.TEXT_FIELD,
                BindParent: { drp_sales_ret: "mobile" },
                IsVerticalView: true,
                inputType: InputType.PHONE,
              }),
              legacyField({
                IncludeLabel: true,
                Label: "TRN.",
                BindValue: "txt_customer_taxregno",
                Json_Attribute: "customer_taxregno",
                componentType: ComponentType.TEXT_FIELD,
                IsReadOnly: true,
                BindParent: { drp_sales_ret: "trn_no" },
                IsVerticalView: true,
                inputType: InputType.TEXT,
              }),
              legacyField({
                IncludeLabel: true,
                Label: "Customer name",
                BindValue: "txt_customer_name",
                Json_Attribute: "customer_name",
                componentType: ComponentType.TEXT_FIELD,
                IsReadOnly: true,
                BindParent: { drp_sales_ret: "customername" },
                IsVerticalView: true,
                inputType: InputType.TEXT,
              }),
              legacyField({
                IncludeLabel: true,
                Label: "Ref No.",
                BindValue: "txt_customer_refno",
                Json_Attribute: "customer_refno",
                componentType: ComponentType.TEXT_FIELD,
                IsVerticalView: true,
                inputType: InputType.TEXT,
              }),
              legacyField({
                IncludeLabel: true,
                Label: "Ref date",
                BindValue: "txt_sales_ref_date",
                Json_Attribute: "ref_date",
                componentType: ComponentType.DATE_FIELD,
                DefaultToday: true,
                IsVerticalView: true,
              }),
              legacyField({
                IncludeLabel: true,
                Label: "Fax",
                BindValue: "txt_fax",
                Json_Attribute: "fax",
                componentType: ComponentType.TEXT_FIELD,
                BindParent: { drp_sales_ret: "fax" },
                IsVerticalView: true,
                inputType: InputType.TEXT,
              }),
              legacyField({
                IncludeLabel: true,
                Label: "Remarks",
                BindValue: "txt_remarks",
                Json_Attribute: "remarks",
                componentType: ComponentType.TEXT_FIELD,
                IsVerticalView: true,
                inputType: InputType.TEXT,
              }),
              legacyField({
                IncludeLabel: true,
                Label: "Return amount",
                BindValue: "txt_return_amount",
                Json_Attribute: "returned_amount",
                componentType: ComponentType.TEXT_FIELD,
                IsReadOnly: true,
                Default_Value: "0",
                IsVerticalView: true,
                inputType: InputType.NUMBER,
                ExpressionV2:
                  "[return_spare_net] + [return_labour_net] + [return_misc_net]",
              }),
              legacyField({
                IncludeLabel: true,
                Label: "Return VAT",
                BindValue: "txt_return_vat_amount",
                Json_Attribute: "return_vat_amount",
                componentType: ComponentType.TEXT_FIELD,
                IsReadOnly: true,
                Default_Value: "0",
                IsVerticalView: true,
                inputType: InputType.NUMBER,
                ExpressionV2:
                  "[return_spare_vat] + [return_labour_vat] + [return_misc_vat]",
              }),
              legacyField({
                IncludeLabel: true,
                Label: "Add to Unadjusted Entries",
                BindValue: "chk_add_to_unadjusted",
                Json_Attribute: "is_draft",
                componentType: ComponentType.CHECKBOX,
                IsVerticalView: true,
                Default_Value: "0",
              }),
            ],
          },
          buildSpareSubmodule(),
          buildLabourSubmodule(),
          buildMiscSubmodule(),
        ],
      },
      {
        componentType: ComponentType.BUTTON,
        name: "Submit",
        onPressAction: "save",
        style: {
          backgroundColor: "#22c55e",
          textColor: "#FFFFFF",
          padding: 12,
          borderRadius: 8,
        },
      },
    ],
  },
];
